import { TimeInput as MantineTimeInput } from "@mantine/dates";
import React, { memo, useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "../../../redux/hooks";
import { setEditing } from "../../../redux/slices/entrySlice";
import {
  setTimeInputPopup,
  setTriggerTimeCheck,
} from "../../../redux/slices/generatorFeaturesSlice";
import { useLogbookRefsContext } from "../../../generator/Logbook/LogbookRefsContext";

interface Props {
  minTime?: string;
  maxTime?: string;
  onInputChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  value: string;
  id: string;
  className?: string;
  dataId?: string;
  error?: boolean;
  button?: boolean;
}

const TimeInput = ({
  value,
  onInputChange,
  onKeyDown,
  minTime,
  maxTime,
  id,
  className,
  dataId,
  error,
  button,
}: Props) => {
  const dispatch = useDispatch();

  const inputRef = useRef<HTMLInputElement>(null);
  const typingRef = useRef<boolean>(false);

  // Get the centralized tab navigation props
  const { ref: addToRefs, onKeyDown: handleLogbookKeyDown } =
    useLogbookRefsContext(id);
  const handleOpenPopup = () => {
    if (inputRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const spaceAbove = inputRect.top;
      const popupHeight = 250;

      let top = inputRect.bottom - 100; // bottom position
      // Check if there's enough space above the input
      if (spaceAbove > popupHeight + 100) {
        top = inputRect.top - popupHeight - (button ? 102 : 110);
      }
      dispatch(
        setTimeInputPopup({
          id,
          value,
          onChange: onInputChange,
          top,
          left: inputRect.left - (button ? 25 : 0),
          minTime,
          maxTime,
        }),
      );
    }
  };

  const handleTyping = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    typingRef.current = true;
    onInputChange(newValue);
  };
  const handleBlur = () => {
    dispatch(setEditing(true));
    if (typingRef.current) dispatch(setTriggerTimeCheck(Math.random()));
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // First, let the centralized tab navigation system handle the event
    handleLogbookKeyDown(e);

    // Then call the original onKeyDown handler if provided
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <MantineTimeInput
      ref={ref => {
        if (inputRef.current !== ref) {
          (inputRef as any).current = ref;
        }
        addToRefs(ref);
      }}
      value={value}
      data-id={dataId}
      data-unit={"time"}
      onChange={handleTyping}
      onClick={handleOpenPopup}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={className}
      error={error}
      styles={
        button
          ? {
              root: {
                opacity: 0,
                position: "absolute",
                width: 52,
                height: 22,
                marginTop: 2,
                transform: "translate(-25%, -50%)",
              },
              wrapper: {
                width: "100%",
                height: "100%",
              },
              input: {
                minHeight: 0,
                width: "100%",
                height: "100%",
                padding: 0,
                cursor: "pointer",
              },
            }
          : {}
      }
    />
  );
};

export default memo(TimeInput);
